/**
 * Careers page (2026-10-08) — PUBLIC job detail at /careers/:slug (slug, or the
 * old opening id). Full JD in 4 parts + Apply + Share on WhatsApp. The server
 * already put the JobPosting JSON-LD into the HTML (careers-page.ts); this page
 * renders the same data. All text is rendered as React text (escaped) — no
 * dangerouslySetInnerHTML. Apply: name, 10-digit mobile, city, resume optional;
 * ?src / ?p from the link go with it. Mobile-first.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Briefcase, CheckCircle, Clock, MapPin, MessageCircle, UploadCloud, Users, X } from 'lucide-react';
import { CareersChrome } from './CareersChrome';
import { fetchJob, submitApplication, type Company, type PublicJob } from './careersPublicApi';
import { formatEmploymentType, formatExperienceRange, formatPostedDate } from './careersFormat';
import { formatSalaryLakh, readTracking, validateCareerApply, type CareerApplyInput } from './careersPure';

const PARTS: [keyof PublicJob['jd'], string][] = [
  ['about', 'About the role'],
  ['whatYouWillDo', 'What you will do'],
  ['whoWeAreLookingFor', 'Who we are looking for'],
  ['whatWeOffer', 'What we offer'],
];

export function CareerDetailPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const { search } = useLocation();
  const [company, setCompany] = useState<Company | null>(null);
  const [job, setJob] = useState<PublicJob | null>(null);
  const [error, setError] = useState('');
  const applyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    setJob(null);
    setError('');
    fetchJob(slug)
      .then((r) => {
        if (!alive) return;
        setCompany(r.company);
        setJob(r.job);
        document.title = `${r.job.jobTitle} · ${r.job.location} · ${r.company.name}`;
      })
      .catch((e) => alive && setError(e.message));
    return () => {
      alive = false;
    };
  }, [slug]);

  const tracking = readTracking(search);
  const shareUrl = job ? `${window.location.origin}/careers/${job.slug}?src=whatsapp` : '';
  const shareText = job ? `${company?.name ?? ''} is hiring: ${job.jobTitle}, ${job.location}. Apply here: ${shareUrl}` : '';

  return (
    <CareersChrome company={company}>
      <Link to="/careers" className="mb-4 inline-flex min-h-[40px] items-center gap-1 text-sm font-semibold text-gray-500 hover:text-gray-900">
        <ArrowLeft className="h-4 w-4" /> All jobs
      </Link>

      {error ? (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-white py-16 text-center">
          <AlertCircle className="mx-auto mb-3 h-8 w-8 text-gray-300" />
          <p className="font-semibold text-gray-700">{error}</p>
          <Link to="/careers" className="mt-3 inline-block text-sm font-semibold text-[#1F5C3A]">
            See other jobs
          </Link>
        </div>
      ) : !job ? (
        <div className="py-16 text-center text-gray-400">Loading…</div>
      ) : (
        <div className="space-y-5">
          <article className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6" data-tour="careers-jd">
            <div className="text-xs text-gray-500">Careers › {job.jobTitle}</div>
            <h1 className="mt-1 font-display text-2xl font-bold text-gray-900">{job.jobTitle}</h1>
            <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-600">
              <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-1">
                <MapPin className="h-3 w-3" />
                {job.location}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-1">
                <Briefcase className="h-3 w-3" />
                {formatEmploymentType(job.employmentType)}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-1">
                <Users className="h-3 w-3" />
                {job.vacancies} vacanc{job.vacancies === 1 ? 'y' : 'ies'}
              </span>
              {job.salary && (formatSalaryLakh(job.salary.minLakh, job.salary.maxLakh) || null) && (
                <span className="rounded-full bg-gray-50 px-2 py-1">{formatSalaryLakh(job.salary.minLakh, job.salary.maxLakh)}</span>
              )}
              {job.experience && formatExperienceRange(job.experience.min, job.experience.max) && (
                <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-1">
                  <Clock className="h-3 w-3" />
                  {formatExperienceRange(job.experience.min, job.experience.max)}
                </span>
              )}
            </div>
            {job.postedAt && <p className="mt-2 text-xs text-gray-400">Posted {formatPostedDate(job.postedAt)}</p>}
            <div className="mt-5 space-y-4">
              {PARTS.map(([k, label]) =>
                job.jd[k] ? (
                  <section key={k}>
                    <h2 className="text-base font-semibold text-gray-900">{label}</h2>
                    <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-gray-700">{job.jd[k]}</p>
                  </section>
                ) : null,
              )}
            </div>
            <div className="mt-6 grid grid-cols-1 gap-2 sm:flex">
              <button
                onClick={() => applyRef.current?.scrollIntoView({ behavior: 'smooth' })}
                className="min-h-[44px] rounded-xl bg-[#1F5C3A] px-5 text-sm font-semibold text-white hover:bg-[#184a2e]"
                data-tour="careers-apply-btn"
              >
                Apply for this job
              </button>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-[#1F5C3A] px-5 text-sm font-semibold text-[#1F5C3A] hover:bg-green-50"
              >
                <MessageCircle className="h-4 w-4" /> Share on WhatsApp
              </a>
            </div>
          </article>
          <div ref={applyRef}>
            <CareerApplyForm job={job} tracking={tracking} />
          </div>
        </div>
      )}
    </CareersChrome>
  );
}

const inputCls =
  'w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-base text-gray-900 placeholder-gray-400 focus:border-[#1F5C3A] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1F5C3A]/20 sm:text-sm';

function CareerApplyForm({ job, tracking }: { job: PublicJob; tracking: { src?: string; p?: string } }) {
  const [f, setF] = useState<CareerApplyInput>({ applicantName: '', phone: '', city: '' });
  const [hp, setHp] = useState('');
  const [errors, setErrors] = useState<Partial<Record<keyof CareerApplyInput, string>>>({});
  const [formErr, setFormErr] = useState('');
  const [resume, setResume] = useState<File | null>(null);
  const [stage, setStage] = useState<'form' | 'sending' | 'done'>('form');
  const fileRef = useRef<HTMLInputElement>(null);

  function pick(file: File | null) {
    setFormErr('');
    if (!file) return setResume(null);
    if (file.size > 5 * 1024 * 1024) {
      setFormErr('Resume must be 5 MB or smaller');
      return;
    }
    if (!/\.(pdf|docx?|jpe?g|png)$/i.test(file.name)) {
      setFormErr('Resume must be a PDF, Word file or a photo (JPG/PNG)');
      return;
    }
    setResume(file);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const v = validateCareerApply(f);
    setErrors(v.errors);
    if (!v.ok) return;
    setStage('sending');
    setFormErr('');
    try {
      const r = await submitApplication({ job: job.slug, ...f, ...tracking, website: hp }, resume);
      void r;
      setStage('done');
    } catch (err: any) {
      setFormErr(err?.message || 'Could not send. Please try again.');
      setStage('form');
    }
  }

  if (stage === 'done') {
    return (
      <div className="rounded-2xl border border-green-200 bg-green-50 p-6 text-center">
        <CheckCircle className="mx-auto mb-2 h-8 w-8 text-green-600" />
        <p className="font-semibold text-gray-900">Application sent. Thank you!</p>
        <p className="mt-1 text-sm text-gray-600">Our HR team will call you on the number you gave.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6" data-tour="careers-apply-form">
      <h2 className="font-display text-lg font-bold text-gray-900">Apply · {job.jobTitle}</h2>
      {formErr && (
        <div className="mt-3 flex items-start gap-2 rounded-xl bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          {formErr}
        </div>
      )}
      <div className="mt-4 space-y-3">
        <Field label="Full name" error={errors.applicantName}>
          <input className={inputCls} autoComplete="name" maxLength={120} value={f.applicantName} onChange={(e) => setF({ ...f, applicantName: e.target.value })} placeholder="Your name" />
        </Field>
        <Field label="Mobile" error={errors.phone}>
          <input className={inputCls} type="tel" inputMode="tel" autoComplete="tel" maxLength={16} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="10-digit number" />
        </Field>
        <Field label="City" error={errors.city}>
          <input className={inputCls} autoComplete="address-level2" maxLength={80} value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} placeholder="City" />
        </Field>
        {/* honeypot: hidden from people, bots fill it */}
        <div aria-hidden="true" className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden">
          <label>
            Website
            <input tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} name="website" />
          </label>
        </div>
        <div>
          <span className="mb-1 block text-sm font-medium text-gray-700">Resume (optional)</span>
          {resume ? (
            <div className="flex min-h-[44px] items-center justify-between gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm">
              <span className="truncate">{resume.name}</span>
              <button type="button" aria-label="Remove resume" className="p-2 text-gray-400" onClick={() => setResume(null)}>
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 text-sm text-gray-600 hover:bg-gray-50"
            >
              <UploadCloud className="h-4 w-4" /> Upload PDF, Word or a photo
            </button>
          )}
          <input ref={fileRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={(e) => pick(e.target.files?.[0] ?? null)} />
        </div>
      </div>
      <button
        type="submit"
        disabled={stage === 'sending'}
        className="mt-5 min-h-[48px] w-full rounded-xl bg-[#1F5C3A] text-sm font-semibold text-white hover:bg-[#184a2e] disabled:opacity-60"
        data-tour="careers-submit"
      >
        {stage === 'sending' ? 'Sending…' : 'Submit application'}
      </button>
    </form>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
      {children}
      {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
    </label>
  );
}

export default CareerDetailPage;
